import { Injectable, inject } from '@angular/core';
import { AppAuthService } from '../auth';
import { environment } from '../../../environments/environment';
import { ChatRequestError, chatErrorFromResponse, chatErrorFromSseEvent } from './help-chat-errors';
const CHAT_API_BASE_URL = environment.chatServiceBaseUrl;

/**
 * Budget for POST /messages, which is far slower to answer than it looks.
 *
 * The service's `handleTurn` only writes the SSE response headers at step 12 —
 * after the moderator LLM call (which itself retries once on a provider
 * overload) and after KB retrieval. Until then `fetch` stays unresolved, so the
 * client abort timer is really a time-to-first-header budget, not a stream
 * budget. The 15 s default the small JSON calls use sits below the service's
 * own 30 s STREAM_IDLE_TIMEOUT_MS, which would let the client kill a turn the
 * service still considers healthy.
 */
const SEND_TIMEOUT_MS = 60_000;

export interface ChatSession {
  sessionId: string;
  userId: string;
  status: string;
  messageCount: number;
  summaryPresent: boolean;
  createdAt: string;
  lastActivityAt: string;
  previousSession?: {
    id: string;
    archivedAt: string;
    archiveReason: 'inactivity';
    preview: string;
  } | null;
}

export interface ChatSessionListEntry {
  id: string;
  status: 'active' | 'archived';
  createdAt: string;
  archivedAt: string | null;
  archiveReason: 'user' | 'inactivity' | null;
  messageCount: number;
  preview: string;
}

export interface ChatContext {
  pageUrl: string;
  pageType: string;
  customerId: string;
  locale: string;
  availableActions?: PerTurnAction[];
}

export interface PerTurnAction {
  id: string;
  basedOn?: string;
  label?: string;
  params?: Record<string, unknown>;
}

export interface ProposedAction {
  id: string;
  label: string;
  params?: Record<string, unknown>;
  category: 'support' | 'account' | 'navigation';
  offset: number;
  messageId: string;
}

export interface ChatMessageRecord {
  id: string;
  sessionId: string;
  role: 'user' | 'assistant' | string;
  content: string;
  idempotencyKey?: string | null;
  createdAt: string;
  streamingStatus?: 'streaming' | 'done' | 'error' | null;
  actionsProposed?: Array<ProposedAction & { latestOutcome: string | null }> | null;
  citations?: Array<{ sourcePath: string; url?: string }> | null;
}

export interface ChatHistoryResponse {
  sessionId: string;
  messages: ChatMessageRecord[];
}

@Injectable({ providedIn: 'root' })
export class HelpChatService {
  private readonly authService = inject(AppAuthService);

  private authHeaders() {
    const token = this.authService.user?.token;
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  /**
   * Every chat call goes through here so a dead network, a DNS failure or a
   * CORS rejection surfaces as a `ChatRequestError` like any other failure —
   * `fetch` rejects with a bare `TypeError` that carries no status and reads
   * as an internal bug to whatever catches it.
   */
  private async request(path: string, init: RequestInit, timeoutMs = 15000): Promise<Response> {
    const controller = new AbortController();
    const timer = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
    try {
      return await fetch(`${CHAT_API_BASE_URL}${path}`, { ...init, signal: controller.signal });
    } catch (err) {
      // Our own abort timer firing is a timeout, not a dead network — telling a
      // user to "check your network" when the assistant is merely slow sends
      // them debugging the wrong thing.
      throw controller.signal.aborted
        ? new ChatRequestError(0, 'request_timeout', 'request_timeout')
        : new ChatRequestError(0, 'network', (err as Error)?.message ?? null);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  async getSession(): Promise<ChatSession | null> {
    const response = await this.request('/session', {
      method: 'GET',
      headers: {
        ...this.authHeaders(),
      },
    });
    if (response.status === 404) return null;
    if (!response.ok) throw await chatErrorFromResponse(response);
    return await response.json();
  }

  async resetSession(): Promise<void> {
    const response = await this.request('/session', { method: 'DELETE', headers: { ...this.authHeaders() } });
    if (!response.ok && response.status !== 404) throw await chatErrorFromResponse(response);
  }

  async getMessages(limit = 20, before?: string): Promise<ChatHistoryResponse> {
    const ts = Date.now();
    const cursor = before ? `&before=${encodeURIComponent(before)}` : '';
    const response = await this.request(`/messages?limit=${limit}&_ts=${ts}${cursor}`, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        ...this.authHeaders(),
      },
    });
    if (!response.ok) throw await chatErrorFromResponse(response);
    return await response.json();
  }

  async openStream(handlers: {
    onEvent?: (event: string, payload: any) => void;
    onError?: (error: string) => void;
  }): Promise<() => void> {
    // This is an intentionally long-lived SSE connection; it must not use
    // the normal request timeout used by session/history calls.
    const response = await this.request(
      '/stream',
      {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          ...this.authHeaders(),
        },
      },
      0
    );
    if (!response.ok || !response.body) throw await chatErrorFromResponse(response);

    let closed = false;
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const processBlock = (block: string) => {
      const lines = block.split('\n');
      let event = '';
      let data = '';
      for (const line of lines) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (!data) return;
      try {
        const parsed = JSON.parse(data);
        handlers.onEvent?.(event || 'message', parsed);
      } catch {
        handlers.onError?.('Failed to parse chat stream event');
      }
    };

    void (async () => {
      try {
        while (!closed) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const blocks = buffer.split('\n\n');
          buffer = blocks.pop() ?? '';
          for (const block of blocks) processBlock(block);
        }
      } catch {
        if (!closed) handlers.onError?.('Chat stream disconnected');
      } finally {
        try {
          await reader.cancel();
        } catch {
          // ignore
        }
      }
    })();

    return () => {
      closed = true;
    };
  }

  async streamMessage(
    message: string,
    context: ChatContext,
    idempotencyKeyOrHandlers: string | undefined | {
      onMeta?: (meta: any) => void;
      onDelta?: (text: string) => void;
      onCitation?: (citation: { sourcePath: string; url?: string }) => void;
      onAction?: (action: ProposedAction) => void;
      onUsage?: (usage: any) => void;
      onDone?: (done: any) => void;
      onError?: (error: ChatRequestError) => void;
    },
    maybeHandlers?: {
      onMeta?: (meta: any) => void;
      onDelta?: (text: string) => void;
      onCitation?: (citation: { sourcePath: string; url?: string }) => void;
      onAction?: (action: ProposedAction) => void;
      onUsage?: (usage: any) => void;
      onDone?: (done: any) => void;
      onError?: (error: ChatRequestError) => void;
    }
  ) {
    const idempotencyKey = typeof idempotencyKeyOrHandlers === 'string' ? idempotencyKeyOrHandlers : undefined;
    const handlers = typeof idempotencyKeyOrHandlers === 'string' || idempotencyKeyOrHandlers === undefined
      ? maybeHandlers! : idempotencyKeyOrHandlers;
    const response = await this.request(
      '/messages',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(idempotencyKey ? {'Idempotency-Key': idempotencyKey} : {}),
          ...this.authHeaders(),
        },
        body: JSON.stringify({ message, context }),
      },
      SEND_TIMEOUT_MS
    );

    // A rejected turn (moderation, rate limit, budget, concurrent stream) never
    // opens the stream — it answers with a JSON body carrying `code`. Callers
    // need that code to say something useful, so parse it instead of throwing
    // the status on its own.
    if (!response.ok || !response.body) throw await chatErrorFromResponse(response);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = '';
    let finished = false;

    const emitBlock = (block: string) => {
      const lines = block.split('\n');
      let event = currentEvent;
      let data = '';
      for (const line of lines) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (!event || !data) return;
      try {
        const parsed = JSON.parse(data);
        if (event === 'meta') handlers.onMeta?.(parsed);
        if (event === 'delta') handlers.onDelta?.(parsed.text ?? '');
        if (event === 'citation') handlers.onCitation?.(parsed);
        if (event === 'action') handlers.onAction?.(parsed as ProposedAction);
        if (event === 'usage') handlers.onUsage?.(parsed);
        // `parsed.message` is the raw provider/exception text — keep it on the
        // error object for logging, but let the caller pick what to display.
        if (event === 'error') handlers.onError?.(chatErrorFromSseEvent(parsed, response.status));
        if (event === 'done') {
          handlers.onDone?.(parsed);
          finished = true;
        }
      } catch {
        handlers.onError?.(new ChatRequestError(response.status, 'parse_error'));
        finished = true;
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() ?? '';
      for (const block of blocks) emitBlock(block);
      if (finished) {
        reader.cancel().catch(() => undefined);
        return;
      }
    }

    if (buffer.trim()) emitBlock(buffer.trim());
    if (finished) {
      reader.cancel().catch(() => undefined);
      return;
    }
  }

  // Fire-and-forget click telemetry. We never throw from here — a failure to
  // record a click should NOT block the action handler. The server applies
  // an integrity check (the (messageId, actionId, params) triple must match
  // a server-recorded proposal) so unauthorized clicks are rejected.
  async recordActionClick(args: {
    messageId: string;
    actionId: string;
    params?: Record<string, unknown>;
    outcome?: 'success' | 'failed' | 'cancelled';
  }): Promise<void> {
    try {
      const body = JSON.stringify({
        messageId: args.messageId,
        actionId: args.actionId,
        params: args.params ?? {},
        clientTs: new Date().toISOString(),
        ...(args.outcome ? { outcome: args.outcome } : {}),
      });
      await fetch(`${CHAT_API_BASE_URL}/actions/click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.authHeaders(),
        },
        body,
      });
    } catch (err) {
      console.warn('[help-chat] failed to record click', err);
    }
  }

  // Heartbeat. Swallows errors so a transient network hiccup never crashes
  // the widget — the server tolerates lost pings (it sweeps after 30 min,
  // pings happen every 5 min, so ~6 pings can be lost before archival).
  async ping(): Promise<void> {
    try {
      await fetch(`${CHAT_API_BASE_URL}/session/ping`, {
        method: 'POST',
        headers: { ...this.authHeaders() },
      });
    } catch (err) {
      console.warn('[help-chat] ping failed', err);
    }
  }

  async listSessions(): Promise<ChatSessionListEntry[]> {
    const response = await this.request('/sessions', {
      method: 'GET',
      headers: { ...this.authHeaders() },
    });
    if (!response.ok) throw await chatErrorFromResponse(response);
    const body = (await response.json()) as { sessions: ChatSessionListEntry[] };
    return body.sessions;
  }

  async activateSession(id: string): Promise<{ sessionId: string; previousSessionId: string | null }> {
    const response = await this.request(`/sessions/${encodeURIComponent(id)}/activate`, {
      method: 'POST',
      headers: { ...this.authHeaders() },
    });
    if (!response.ok) throw await chatErrorFromResponse(response);
    return await response.json();
  }
}
